import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  Form,
  Switch,
  Select,
  Button,
  message,
  Space,
  Alert,
  Divider,
  Row,
  Col,
  Input,
  InputNumber,
  Table,
  Modal,
  Grid,
  Tag
} from 'antd';
import {
  ThunderboltOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import { SizeInput } from '../../../shared/components/cdn/UnitInputs';

const { Option } = Select;
const { useBreakpoint } = Grid;

export default function Optimization() {
  const { t } = useTranslation();
  const { domainData, reloadDomain } = useOutletContext();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  // 用 useWatch 订阅开关值，保证切换「启用 Early Hints」时下方资源区能即时显隐
  const earlyHintsEnabled = Form.useWatch('early_hints_enabled', form);
  // 订阅 WebP 开关，控制细控项显隐
  const webpEnabled = Form.useWatch('webp_enabled', form);

  // Early Hints 配置
  const [earlyHintsLinks, setEarlyHintsLinks] = useState([]);
  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [linkForm] = Form.useForm();

  useEffect(() => {
    if (domainData) {
      // 从domain数据或rules中提取优化设置
      const settings = domainData.optimization_settings || {};

      form.setFieldsValue({
        minify_html: settings.minify_html || false,
        minify_css: settings.minify_css || false,
        minify_js: settings.minify_js || false,
        gzip_enabled: settings.gzip_enabled !== undefined ? settings.gzip_enabled : true,
        brotli_enabled: settings.brotli_enabled || false,
        zstd_enabled: settings.zstd_enabled || false,
        // 压缩细控（0 = 使用边缘库默认级别；min/max = 字节，0 = 库默认门限）
        gzip_level: settings.gzip_level ?? 0,
        brotli_level: settings.brotli_level ?? 0,
        zstd_level: settings.zstd_level ?? 0,
        compress_min_length: settings.compress_min_length ?? 0,
        compress_max_length: settings.compress_max_length ?? 0,
        compress_types: Array.isArray(settings.compress_types) ? settings.compress_types : [],
        // 字符集（响应 Content-Type charset 覆盖，对齐边缘 charset 模块）
        charset_enabled: settings.charset_enabled || false,
        charset: settings.charset || 'utf-8',
        charset_force: settings.charset_force || false,
        charset_uppercase: settings.charset_uppercase || false,
        charset_content_types: Array.isArray(settings.charset_content_types) ? settings.charset_content_types : [],
        http2_enabled: settings.http2_enabled !== undefined ? settings.http2_enabled : true,
        http3_enabled: settings.http3_enabled ?? true, // on unless explicitly turned off
        websocket_compression: settings.websocket_compression || false,
        // WebP 转码（边缘 buildWebpConfig 读取 webp_*）
        webp_enabled: settings.webp_enabled || false,
        webp_quality: settings.webp_quality ?? 80,
        webp_lossless: settings.webp_lossless || false,
        webp_min_length: settings.webp_min_length ?? 0,
        webp_max_length: settings.webp_max_length ?? 0
      });

      // 加载 Early Hints 配置
      const earlyHintsEnabled = domainData.early_hints_enabled || false;
      const links = domainData.early_hints_links || [];

      form.setFieldsValue({
        early_hints_enabled: earlyHintsEnabled
      });

      // 解析 early_hints_links
      if (typeof links === 'string') {
        try {
          setEarlyHintsLinks(JSON.parse(links));
        } catch (e) {
          setEarlyHintsLinks([]);
        }
      } else if (Array.isArray(links)) {
        setEarlyHintsLinks(links);
      }
    }
  }, [domainData, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // 保存到domain的优化设置中
      const response = await cdnService.updateDomain({
        id: domainData.id,
        optimization_settings: {
          minify_html: values.minify_html,
          minify_css: values.minify_css,
          minify_js: values.minify_js,
          gzip_enabled: values.gzip_enabled,
          brotli_enabled: values.brotli_enabled,
          zstd_enabled: values.zstd_enabled,
          gzip_level: values.gzip_level ?? 0,
          brotli_level: values.brotli_level ?? 0,
          zstd_level: values.zstd_level ?? 0,
          compress_min_length: values.compress_min_length ?? 0,
          compress_max_length: values.compress_max_length ?? 0,
          compress_types: Array.isArray(values.compress_types) ? values.compress_types : [],
          charset_enabled: values.charset_enabled,
          charset: values.charset,
          charset_force: values.charset_force,
          charset_uppercase: values.charset_uppercase,
          charset_content_types: Array.isArray(values.charset_content_types) ? values.charset_content_types : [],
          http2_enabled: true,
          http3_enabled: values.http3_enabled,
          webp_enabled: values.webp_enabled,
          webp_quality: values.webp_quality ?? 80,
          webp_lossless: values.webp_lossless,
          webp_min_length: values.webp_min_length ?? 0,
          webp_max_length: values.webp_max_length ?? 0
        },
        early_hints_enabled: values.early_hints_enabled,
        early_hints_links: earlyHintsLinks
      });

      if (response.success) {
        message.success(t('Saved successfully'));
        reloadDomain();
      } else {
        message.error(response.error || t('Failed to save'));
      }
    } catch (error) {
      message.error(t('Failed to save'));
    } finally {
      setLoading(false);
    }
  };

  // Early Hints 链接管理
  const handleAddLink = () => {
    setEditingLink(null);
    linkForm.resetFields();
    setLinkModalVisible(true);
  };

  const handleEditLink = (link, index) => {
    setEditingLink(index);
    linkForm.setFieldsValue(link);
    setLinkModalVisible(true);
  };

  const handleDeleteLink = (index) => {
    const newLinks = earlyHintsLinks.filter((_, i) => i !== index);
    setEarlyHintsLinks(newLinks);
    message.success(t('Deleted'));
  };

  const handleSaveLink = async () => {
    try {
      const values = await linkForm.validateFields();

      if (editingLink !== null) {
        // 编辑现有链接
        const newLinks = [...earlyHintsLinks];
        newLinks[editingLink] = values;
        setEarlyHintsLinks(newLinks);
      } else {
        // 添加新链接
        setEarlyHintsLinks([...earlyHintsLinks, values]);
      }

      setLinkModalVisible(false);
      linkForm.resetFields();
      message.success(editingLink !== null ? t('Updated') : t('Added'));
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // Mobile card renderer for Early Hints
  const renderMobileEarlyHintsCard = (link, index) => {
    const typeMap = {
      style: 'CSS',
      script: 'JavaScript',
      font: t('Font'),
      image: t('Image'),
      fetch: t('Data'),
      document: 'HTML'
    };

    return (
      <Card key={index} size="small" style={{ marginBottom: 8 }}>
        <div style={{ marginBottom: 8 }}>
          <div style={{ wordBreak: 'break-all', fontWeight: 500, marginBottom: 4 }}>
            {link.url}
          </div>
          <Space wrap size="small">
            <Tag color="blue">{typeMap[link.as] || link.as}</Tag>
            {link.type && <Tag>{link.type}</Tag>}
            {link.crossorigin && <Tag color="orange">{link.crossorigin}</Tag>}
          </Space>
        </div>
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditLink(link, index)}>{t('Edit')}</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteLink(index)}>{t('Delete')}</Button>
        </Space>
      </Card>
    );
  };

  return (
    <div style={{ padding: isMobile ? '12px 0' : 0 }}>
      <Card
        title={
          <Space>
            <ThunderboltOutlined />{t('Performance optimization')}</Space>
        }
        extra={
          !isMobile && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSubmit}
              loading={loading}
            >{t('Save settings')}</Button>
          )
        }
        size={isMobile ? 'small' : 'default'}
      >
        {isMobile && (
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSubmit}
              loading={loading}
              size="small"
              block
            >{t('Save settings')}</Button>
          </div>
        )}

        <Alert
          message={t('Performance optimization')}
          description={isMobile ? t('Enable these features to improve website load speed') : t('These features can significantly improve website load speed and user experience. Some may need testing to ensure compatibility with your website.')}
          type="info"
          showIcon
          style={{ marginBottom: isMobile ? 16 : 24 }}
        />

        <Form
          form={form}
          layout="vertical"
        >
          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>{t('Content compression')}</Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? "HTML" : t('Minify HTML')}
                name="minify_html"
                valuePropName="checked"
                tooltip={!isMobile ? t('Remove whitespace and comments from HTML to reduce file size') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? "CSS" : t('Minify CSS')}
                name="minify_css"
                valuePropName="checked"
                tooltip={!isMobile ? t('Remove whitespace and comments from CSS to reduce file size') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? "JS" : t('Minify JavaScript')}
                name="minify_js"
                valuePropName="checked"
                tooltip={!isMobile ? t('Remove whitespace and comments from JS to reduce file size') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>{t('Transfer compression')}</Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label="Gzip"
                name="gzip_enabled"
                valuePropName="checked"
                tooltip={!isMobile ? t('Compress responses with Gzip, typically reducing transfer size by 70%') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label="Brotli"
                name="brotli_enabled"
                valuePropName="checked"
                tooltip={!isMobile ? t('Compress with Brotli, which is more efficient than Gzip') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label="Zstandard"
                name="zstd_enabled"
                valuePropName="checked"
                tooltip={!isMobile ? t('Compress with Zstd for a high compression ratio and low CPU usage (supported by modern browsers)') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>{t('Advanced compression settings')}</Divider>

          <Alert
            message={isMobile ? t('Leave at 0 to use the recommended default') : t('Leave each value at 0 to use the recommended edge defaults (gzip 6 / brotli 4 / zstd 3; minimum 1024 bytes, no upper limit, text type allowlist). Change only for fine-tuning.')}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? t('Gzip level') : t('Gzip level (0-9)')}
                name="gzip_level"
                tooltip={!isMobile ? t('1=fastest, 9=highest compression; 0=library default (6)') : undefined}
              >
                <InputNumber min={0} max={9} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? t('Brotli level') : t('Brotli level (0-11)')}
                name="brotli_level"
                tooltip={!isMobile ? t('1=fastest, 11=highest compression; 0=library default (4)') : undefined}
              >
                <InputNumber min={0} max={11} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? t('Zstd level') : t('Zstd level (0-22)')}
                name="zstd_level"
                tooltip={!isMobile ? t('1=fastest, 22=highest compression; 0=library default (3)') : undefined}
              >
                <InputNumber min={0} max={22} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Form.Item
                label={isMobile ? t('Minimum size') : t('Minimum compression size')}
                name="compress_min_length"
                tooltip={!isMobile ? t('Do not compress response bodies smaller than this value; 0 = default of 1024') : undefined}
              >
                <SizeInput presets={[256, 1024, 4096, 10240]} zeroLabel={t('Default (1 KB)')} min={0} max={10485760} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Form.Item
                label={isMobile ? t('Maximum size') : t('Maximum compression size')}
                name="compress_max_length"
                tooltip={!isMobile ? t('Do not compress response bodies larger than this value to avoid excessive CPU usage; 0 = no upper limit') : undefined}
              >
                <SizeInput presets={[1048576, 10485760, 52428800, 104857600]} zeroLabel={t('No limit')} min={0} max={1073741824} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                label={isMobile ? t('Type allowlist') : t('Content type allowlist')}
                name="compress_types"
                tooltip={!isMobile ? t('Compress only these Content-Types (text/* prefixes supported); leave blank for the default text allowlist') : undefined}
              >
                <Select
                  mode="tags"
                  allowClear
                  placeholder="text/html, application/json, text/*"
                  tokenSeparators={[',', ' ']}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>{t('Character set')}</Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={12} sm={8} md={6}>
              <Form.Item
                label={isMobile ? t('Enable') : t('Enable charset override')}
                name="charset_enabled"
                valuePropName="checked"
                tooltip={!isMobile ? t('Add or override the Content-Type charset parameter for text responses') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item
                label={t('Character set')}
                name="charset"
                tooltip={!isMobile ? t('Target encoding, e.g., utf-8, gbk') : undefined}
              >
                <Select showSearch style={{ width: '100%' }}>
                  <Option value="utf-8">utf-8</Option>
                  <Option value="gbk">gbk</Option>
                  <Option value="gb2312">gb2312</Option>
                  <Option value="gb18030">gb18030</Option>
                  <Option value="big5">big5</Option>
                  <Option value="iso-8859-1">iso-8859-1</Option>
                  <Option value="shift_jis">shift_jis</Option>
                  <Option value="euc-jp">euc-jp</Option>
                  <Option value="euc-kr">euc-kr</Option>
                  <Option value="windows-1251">windows-1251</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item
                label={isMobile ? t('Force overwrite') : t('Override origin settings')}
                name="charset_force"
                valuePropName="checked"
                tooltip={!isMobile ? t('Replace charset even if declared by the origin; when off, only add it if the origin does not declare one') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Form.Item
                label={isMobile ? t('Uppercase') : t('Uppercase encoding name')}
                name="charset_uppercase"
                valuePropName="checked"
                tooltip={!isMobile ? t('Output UTF-8 instead of utf-8') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                label={isMobile ? t('Type allowlist') : t('App content type (optional)')}
                name="charset_content_types"
                tooltip={!isMobile ? t('Apply only to these Content-Types; leave blank for default text types') : undefined}
              >
                <Select
                  mode="tags"
                  allowClear
                  placeholder="text/html, application/json"
                  tokenSeparators={[',', ' ']}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>{t('Protocol optimization')}</Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={12} sm={8} md={8}>
              {/* 边缘对所有站点都提供 HTTP/2(按浏览器协商),没有按站点关闭的开关 */}
              <Form.Item label="HTTP/2" tooltip={!isMobile ? t('Always on: browsers negotiate HTTP/2 automatically') : undefined}>
                <Switch checked disabled />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? "HTTP/3" : "HTTP/3 (QUIC)"}
                name="http3_enabled"
                valuePropName="checked"
                tooltip={!isMobile ? t('Enable HTTP/3, based on QUIC, for better performance (experimental)') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>
            {isMobile ? t('WebP conversion') : t('WebP image conversion')}
          </Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? t('Enable') : t('Enable WebP conversion')}
                name="webp_enabled"
                valuePropName="checked"
                tooltip={!isMobile ? t('The edge automatically converts JPEG/PNG to WebP based on the Accept header, typically reducing image size by 25-35%') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? t('Lossless') : t('Lossless compression')}
                name="webp_lossless"
                valuePropName="checked"
                tooltip={!isMobile ? t('Enable lossless WebP (larger files with no quality loss, suitable for icons and line art); the quality setting is ignored when enabled') : undefined}
              >
                <Switch disabled={!webpEnabled} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={8}>
              <Form.Item
                label={isMobile ? t('Quality') : t('Transcoding quality (1-100)')}
                name="webp_quality"
                tooltip={!isMobile ? t('Image quality in lossy mode. Higher values give clearer images and larger files; default 80.') : undefined}
              >
                <InputNumber min={1} max={100} disabled={!webpEnabled} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Form.Item
                label={isMobile ? t('Minimum size') : t('Minimum transcoding size')}
                name="webp_min_length"
                tooltip={!isMobile ? t('Do not convert images smaller than this value; 0 = no lower limit') : undefined}
              >
                <SizeInput presets={[1024, 10240, 51200]} zeroLabel={t('No limit')} min={0} max={10485760} disabled={!webpEnabled} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Form.Item
                label={isMobile ? t('Maximum size') : t('Maximum transcoding size')}
                name="webp_max_length"
                tooltip={!isMobile ? t('Do not convert images larger than this value to avoid excessive CPU usage; 0 = no upper limit') : undefined}
              >
                <SizeInput presets={[1048576, 5242880, 10485760, 20971520]} zeroLabel={t('No limit')} min={0} max={104857600} disabled={!webpEnabled} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>
            {isMobile ? "Early Hints" : "Early Hints (HTTP 103)"}
          </Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col span={24}>
              <Form.Item
                label={t('Enable Early Hints')}
                name="early_hints_enabled"
                valuePropName="checked"
                tooltip={!isMobile ? t('Enable HTTP 103 Early Hints to improve initial page load speed by 20-30%') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {earlyHintsEnabled && (
            <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
              <Col span={24}>
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={handleAddLink}
                    size={isMobile ? 'small' : 'middle'}
                  >
                    {isMobile ? t('Add resource') : t('Add preload resource')}
                  </Button>
                  <Alert
                    message={isMobile ? t('Preload configuration') : t('Configure preload resources')}
                    description={isMobile ? t('Add key resources such as CSS, JS, and fonts; 3-5 are recommended') : t('Add key resources (CSS, JS, fonts, etc.) to preload with Early Hints. We recommend adding only the 3-5 most critical resources.')}
                    type="info"
                    showIcon
                    style={{ marginTop: 12 }}
                  />
                </div>

                {isMobile ? (
                  <div>
                    {earlyHintsLinks.map((link, index) => renderMobileEarlyHintsCard(link, index))}
                    {earlyHintsLinks.length === 0 && (
                      <div style={{ textAlign: 'center', padding: 16, color: '#999' }}>{t('No preloaded resources yet')}</div>
                    )}
                  </div>
                ) : (
                  <Table
                    dataSource={earlyHintsLinks}
                    columns={[
                      {
                        title: 'URL',
                        dataIndex: 'url',
                        key: 'url',
                        width: '35%'
                      },
                      {
                        title: t('Resource type'),
                        dataIndex: 'as',
                        key: 'as',
                        width: '15%',
                        render: (as) => {
                          const typeMap = {
                            style: 'CSS',
                            script: 'JavaScript',
                            font: t('Font'),
                            image: t('Image'),
                            fetch: t('Data'),
                            document: 'HTML'
                          };
                          return typeMap[as] || as;
                        }
                      },
                      {
                        title: t('MIME type'),
                        dataIndex: 'type',
                        key: 'type',
                        width: '20%'
                      },
                      {
                        title: 'CORS',
                        dataIndex: 'crossorigin',
                        key: 'crossorigin',
                        width: '15%'
                      },
                      {
                        title: t('Actions'),
                        key: 'action',
                        width: '15%',
                        render: (_, record, index) => (
                          <Space>
                            <Button
                              type="link"
                              icon={<EditOutlined />}
                              onClick={() => handleEditLink(record, index)}
                            >{t('Edit')}</Button>
                            <Button
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteLink(index)}
                            >{t('Delete')}</Button>
                          </Space>
                        )
                      }
                    ]}
                    pagination={false}
                    size="small"
                    rowKey={(_, index) => index}
                  />
                )}
              </Col>
            </Row>
          )}

          <Alert
            message={t('Notice')}
            description={isMobile ? t('Changes may take a few minutes to take effect') : t('Some optimization changes may take a few minutes to apply across global CDN nodes. Testing during off-peak hours is recommended.')}
            type="warning"
            showIcon
            style={{ marginTop: isMobile ? 16 : 24 }}
          />
        </Form>
      </Card>

      {/* Early Hints 链接编辑Modal */}
      <Modal
        title={editingLink !== null ? t('Edit preload resource') : t('Add preload resource')}
        open={linkModalVisible}
        onOk={handleSaveLink}
        onCancel={() => {
          setLinkModalVisible(false);
          linkForm.resetFields();
        }}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { top: 20 } : undefined}
      >
        <Form
          form={linkForm}
          layout="vertical"
        >
          <Form.Item
            label={t('Resource URL')}
            name="url"
            rules={[
              { required: true, message: t('Enter the resource URL') },
              { pattern: /^\//, message: t('URL must start with /') }
            ]}
            tooltip={t('Relative resource path, e.g. /static/style.css')}
          >
            <Input placeholder="/static/style.css" />
          </Form.Item>

          <Form.Item
            label={t('Resource type (as)')}
            name="as"
            rules={[{ required: true, message: t('Select a resource type') }]}
            tooltip={t('Specify the resource type so the browser can optimize its loading strategy')}
          >
            <Select placeholder={t('Select a resource type')}>
              <Option value="style">{t('CSS stylesheet')}</Option>
              <Option value="script">{t('JavaScript script')}</Option>
              <Option value="font">{t('Font files')}</Option>
              <Option value="image">{t('Image')}</Option>
              <Option value="fetch">{t('Data requests (XHR/Fetch)')}</Option>
              <Option value="document">{t('HTML document')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={t('MIME type (type)')}
            name="type"
            tooltip={t('Optional. Specify the resource\'s MIME type, e.g., font/woff2, image/webp')}
          >
            <Input placeholder="font/woff2" />
          </Form.Item>

          <Form.Item
            label={t('Cross-origin settings (crossorigin)')}
            name="crossorigin"
            tooltip={t('Enable this option for resources that require CORS (such as fonts)')}
          >
            <Select placeholder={t('Select cross-origin settings')} allowClear>
              <Option value="anonymous">{t('anonymous (do not send credentials)')}</Option>
              <Option value="use-credentials">{t('use-credentials (send credentials)')}</Option>
            </Select>
          </Form.Item>

          <Alert
            message={t('Configuration example')}
            description={
              <div>
                <p><strong>CSS:</strong> /static/main.css, as=style</p>
                <p><strong>{t('Font:')}</strong> /fonts/main.woff2, as=font, type=font/woff2, crossorigin=anonymous</p>
                <p><strong>JS:</strong> /static/app.js, as=script</p>
              </div>
            }
            type="info"
            showIcon
          />
        </Form>
      </Modal>
    </div>
  );
}
